import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import siteMessage from "@insite/client-framework/SiteMessage";
import subscribe from "@insite/client-framework/Store/CommonHandlers/Subscribe";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Mail from "@sunSource/Icons/Mail";
import * as cssLinter from "css";
import parse from "html-react-parser";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    label = "label",
    title = "title",
    description = "description",
    placeholder = "placeholder",
    disclaimer = "disclaimer",
    background = "background",
    alignment = "alignment",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.label]: string;
        [fields.description]: string;
        [fields.placeholder]: string;
        [fields.disclaimer]: string;
        [fields.background]: string;
	[fields.alignment]: string;
        [fields.customCSS]: string;
    };
}

const mapDispatchToProps = {
    subscribe,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface SubscribeStyles {
    mainGridContainer: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleLabel?: TypographyPresentationProps;
    descriptionGridItem?: GridItemProps;
    descriptionText?: TypographyPresentationProps;
    emailGridItem?: GridItemProps;
    emailTextField?: TextFieldPresentationProps;
    emailButton?: ButtonPresentationProps;
    disclaimerGridItem?: InjectableCss;
    disclaimerText?: TypographyPresentationProps;
    mailIcon?: TypographyProps;
}

export const subscribeStyles: SubscribeStyles = {
    mainGridContainer: {
        gap: 10,
        css: css`
            width: 100%;
            display: flex;
            flex-direction: row;
            margin: 0 auto;
            &.main-grid-container > div {
                flex-wrap: nowrap;
                align-items: center;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                flex-wrap: wrap;
                            `,
                            null,
                            null,
                            css`
                                flex-wrap: nowrap;
                            `,
                            null,
                        ],
                        "min",
                    )}
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            max-width: 100% !important;
                            padding: 0 30px;
                        `,
                        css`
                            max-width: 100% !important;
                            padding: 0 30px;
                        `,
                        css`
                            max-width: 720px !important;
                            padding: 0 0px;
                        `,
                        css`
                            max-width: 960px !important;
                            padding: 0 0px;
                        `,
                        css`
                            max-width: 1300px !important;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    titleGridItem: {
        width: 12,
        css: css`
             {
                flex-grow: 1;
                position: relative;
            }
        `,
    },
    titleLabel: {
        variant: "h4",
        css: css`
            font-size: 2rem;
            text-transform: uppercase;
            letter-spacing: 1.5px;
            color: #fff;
            margin-left: 15px;
            margin-top: 5px;
        `,
    },
    descriptionGridItem: {
        width: 12,
        css: css`
             {
                flex-grow: 2;
            }
        `,
    },
    descriptionText: {
        css: css`
            margin-bottom: -1rem;
            p {
                font-size: 0.7rem;
                color: #fff;
                text-transform: uppercase;
                line-height: 20px;
            }
        `,
    },
    emailGridItem: {
        width: 12,
        css: css`
             {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                justify-content: flex-start;
                            `,
                            null,
                            null,
                            css`
                                justify-content: flex-end;
                            `,
                            null,
                        ],
                        "min",
                    )}
                span[class^="TypographyStyle-sc"] {
                    position: absolute;
                    bottom: -19px;
                }
            }
        `,
    },
    emailTextField: {
        cssOverrides: {
            formField: css`
                max-width: 255px;
                margin-right: 5px;
            `,
            inputSelect: css`
                border-radius: 3px;
            `,
        },
    },
    emailButton: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 3px;
            padding: 0;
            svg {
                width: 30px;
                margin: 0 7px;
            }
        `,
    },
    // disclaimerGridItem: {
    //     width: 12,
    // },
    disclaimerText: {
        css: css`
            font-size: 12px;
        `,
    },
    mailIcon: {
        css: css`
            width: 40px;
            height: 40px;
        `,
    },
};

const defaultCustomCss = `.main-grid-container{
}

.title-grid-item{
}

.title{
}

.description-grid-item{
}

.description-text{
}

.email-grid-item{
}

.email-text-field{
}

.email-button{
}

.disclaimer-grid-item{
}

.disclaimer-text{
}`;


const DescriptionWrapper = styled.div<{ backgroundColor: string }>`
    background-color: ${props => props.backgroundColor};
    padding: 25px 0;
`;

export const CmsSubscribe = ({ fields, subscribe, id }: Props) => {
    const emailRequiredFieldMessage = siteMessage("EmailSubscription_EmailIsRequiredErrorMessage");
    const emailFieldMessage = siteMessage("EmailSubscription_EmailIsInvalidErrorMessage");

    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState(emailRequiredFieldMessage);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const toasterContext = useContext(ToasterContext);

    const styles = useMergeStyles("subscribe", subscribeStyles);

    const onSubscribeClick = () => {
        setIsSubmitted(true);
        if (!checkEmail(email)) {
            return;
        }

        subscribe({
            email,
            onSuccess: () => {
                toasterContext.addToast({ body: siteMessage("Email_Subscribe_Success"), messageType: "success" });
                setEmail("");
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    this.onSuccess?.();
                }
            },
        });
    };

    const emailChangeHandler = (email: string) => {
        checkEmail(email);
        setEmail(email);
    };

    const checkEmail = (email: string) => {
        const errorMessage = !email ? emailRequiredFieldMessage : validateEmail(email) ? "" : emailFieldMessage;
    setEmailError(errorMessage);
        return !errorMessage;
    };

    const gridItemCss = (gridItemProps?: GridItemProps) => {
        return css`
            justify-content: ${fields.alignment ? fields.alignment : "center"};
            ${gridItemProps?.css}
        `;
    };
    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <DescriptionWrapper backgroundColor={fields.background}>
            <StyledWrapper {...customCssWrapper}>
                <GridContainer className="main-grid-container" {...styles.mainGridContainer}>
                    <GridItem className="title-grid-item" {...styles.titleGridItem}>
                        <Typography {...styles.mailIcon}>
                            <Mail />
                        </Typography>
                        <Typography className="title" {...styles.titleLabel}>
                            {fields.title}
                        </Typography>
                    </GridItem>
                    <GridItem className="description-grid-item" {...styles.descriptionGridItem}>
                        <Typography className="description-text" {...styles.descriptionText}>
                            {parse(fields.description, parserOptions)}
                        </Typography>
                    </GridItem>
                    <GridItem className="email-grid-item" {...styles.emailGridItem}>
                        <TextField
                            className="email-text-field"
                            {...styles.emailTextField}
                            id={id}
                            placeholder={fields.placeholder}
                            value={email}
                            onChange={e => emailChangeHandler(e.currentTarget.value)}
                            error={isSubmitted && emailError}
                        />
                        <Button
                            className="email-button"
                            {...styles.emailButton}
                            onClick={onSubscribeClick}
                            disabled={isSubmitted && !!emailError}
                        >
                            <Mail />
                        </Button>
                    </GridItem>
                </GridContainer>
            </StyledWrapper>
        </DescriptionWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(CmsSubscribe),
    definition: {
        group: "Basic",
        icon: "bell",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.background,
                displayName: "Description Background",
                editorTemplate: "ColorPickerField",
                defaultValue: "white",
                sortOrder: 1,
            },
	    {
                name: fields.alignment,
                displayName: "Layout",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
                defaultValue: "center",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Subscribe",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.description,
                displayName: "Description",
                editorTemplate: "RichTextField",
                defaultValue: "<p>Keep up-to-date on product news and the latest offers.</p>",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.placeholder,
                displayName: "Placeholder Text",
                editorTemplate: "TextField",
                defaultValue: "Enter email address",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.disclaimer,
                displayName: "Disclaimer",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.label,
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "Subscribe",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
